import { useState, useEffect } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X, Star } from "lucide-react";
import { COMPANY, NAV } from "@/lib/siteData";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAvis = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById("avis");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#avis");
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-[#001B35]/95 backdrop-blur-md shadow-[0_4px_20px_-8px_rgba(0,27,53,0.5)]"
          : "bg-[#001B35]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src="/logo-rm.jpg"
              alt="RM Plomberie Chauffage VMC"
              className="h-16 w-16 sm:h-[68px] sm:w-[68px] rounded-xl object-cover ring-2 ring-white/10 group-hover:ring-[#005BBB] transition shadow-lg"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-white font-bold text-xl tracking-tight">
                RM Plomberie
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#9bb6d6]">
                Chauffage • VMC
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/é/g, "e").replace(/à/g, "a").replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                    isActive
                      ? "text-white nav-active"
                      : "text-[#9bb6d6] hover:text-white"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="/#avis"
              onClick={goToAvis}
              data-testid="header-trust-badge"
              aria-label="Voir les avis clients — Ils nous font confiance"
              title="Ils nous font confiance — Voir les avis"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 hover:border-[#FFB400]/60 pl-2.5 pr-3 py-1.5 lg:pr-2.5 2xl:pr-3.5 transition group"
            >
              <span className="flex" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#FFB400] text-[#FFB400]" />
                ))}
              </span>
              <span className="text-[12px] font-semibold text-white uppercase tracking-wider whitespace-nowrap lg:hidden 2xl:inline">
                Ils nous font confiance
              </span>
            </a>
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="header-cta-phone"
              className="hidden md:inline-flex items-center gap-2 border border-white/20 rounded-full pl-4 pr-5 py-2 text-white hover:bg-white hover:text-[#001B35] transition-colors btn-shine"
            >
              <Phone className="w-4 h-4" />
              <span className="font-semibold">{COMPANY.phone}</span>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-white"
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#001B35]" data-testid="mobile-nav">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/é/g, "e").replace(/à/g, "a").replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-sm font-semibold uppercase tracking-wider ${
                    isActive
                      ? "bg-[#005BBB] text-white"
                      : "text-[#9bb6d6] hover:bg-white/5 hover:text-white"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href="/#avis"
              onClick={goToAvis}
              data-testid="mobile-trust-badge"
              className="mt-3 flex items-center justify-center gap-2 rounded-full bg-white/10 border border-white/15 py-3 text-white"
            >
              <span className="flex" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#FFB400] text-[#FFB400]" />
                ))}
              </span>
              <span className="text-sm font-semibold">Ils nous font confiance</span>
            </a>
            <a
              href={`tel:${COMPANY.phoneTel}`}
              className="block mt-3 bg-[#005BBB] text-white text-center font-semibold rounded-full py-3"
              data-testid="mobile-cta-phone"
            >
              <Phone className="inline w-4 h-4 mr-2" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
